// Vyara 2.0 — Hoteles en la parte pública: sección de la página de inicio y página /hoteles.
// Cada hotel se presenta con su banner y algunas de sus zonas comunes, igual que los apartamentos y las experiencias.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Building2 } from 'lucide-react';
import { API_BASE } from '../lib/runtimeConfig';
import { useIsMobile } from '../lib/responsive';

const cop = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-CO')}`;

// La lista se pide una sola vez por visita (la usan el menú, el inicio y /hoteles)
let cache = null;
export const cargarHoteles = () => {
    if (!cache) cache = fetch(`${API_BASE}/api/hoteles/public`).then(r => (r.ok ? r.json() : [])).then(d => (Array.isArray(d) ? d : [])).catch(() => { cache = null; return []; });
    return cache;
};
export const useHotelesPublicos = () => {
    const [hoteles, setHoteles] = useState(null);
    useEffect(() => { let vivo = true; cargarHoteles().then(h => vivo && setHoteles(h)); return () => { vivo = false; }; }, []);
    return hoteles;
};

const HotelCard = ({ hotel, onClick }) => {
    const [hover, setHover] = useState(false);
    const zonas = hotel.zonas || [];
    return (
        <article onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{ borderRadius: 16, overflow: 'hidden', cursor: 'pointer', border: '1px solid #e2e8f0', background: '#fff', transition: 'transform 0.2s, box-shadow 0.2s', transform: hover ? 'translateY(-3px)' : 'none', boxShadow: hover ? '0 12px 30px rgba(15,23,42,0.12)' : 'none', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', height: 200, background: 'linear-gradient(135deg,#003366,#0e7490)' }}>
                {hotel.bannerUrl && <img src={hotel.bannerUrl} alt={hotel.nombre} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
                <span style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(255,255,255,0.94)', color: '#003366', fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 20, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Building2 size={12} /> Hotel</span>
            </div>
            {zonas.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(zonas.length, 4)}, minmax(0, 1fr))`, gap: 2, background: '#fff' }}>
                    {zonas.slice(0, 4).map((z, i) => (
                        <div key={z.url + i} style={{ position: 'relative', height: 62, background: '#f1f5f9' }}>
                            <img src={z.url} alt={z.zona || z.titulo || 'Zona común'} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            {(z.zona || z.titulo) && <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '10px 5px 3px', fontSize: 9.5, fontWeight: 700, color: '#fff', background: 'linear-gradient(transparent, rgba(2,6,23,0.7))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.zona || z.titulo}</span>}
                        </div>
                    ))}
                </div>
            )}
            <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>{hotel.nombre}</div>
                {hotel.ciudad && <div style={{ fontSize: 12.5, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={12} /> {hotel.ciudad}</div>}
                {hotel.descripcionCorta && <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>{hotel.descripcionCorta}</div>}
                {hotel.amenidades?.length > 0 && <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>{hotel.amenidades.slice(0, 4).map(a => <span key={a} style={{ fontSize: 10.5, padding: '2px 8px', background: '#f1f5f9', borderRadius: 20, color: '#475569' }}>{a}</span>)}</div>}
                <div style={{ marginTop: 'auto', paddingTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    {hotel.desde > 0 ? <span><b style={{ fontSize: 15, color: '#1e293b' }}>Desde {cop(hotel.desde)}</b> <span style={{ fontSize: 11.5, color: '#94a3b8' }}>por noche</span></span> : <span />}
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#003366' }}>Ver hotel →</span>
                </div>
            </div>
        </article>
    );
};

// Sección para la página de inicio. `query` filtra por nombre o ciudad (el buscador del inicio).
export const HotelesSection = ({ query = '', titulo = 'Hoteles', estiloTitulo, onCount }) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const hoteles = useHotelesPublicos();
    const q = query.trim().toLowerCase();
    const lista = (hoteles || []).filter(h => !q || `${h.nombre} ${h.ciudad || ''} ${h.descripcionCorta || ''}`.toLowerCase().includes(q));
    useEffect(() => { onCount?.(lista.length); }, [lista.length]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!lista.length) return null;
    return (
        <section id="hoteles" style={{ scrollMarginTop: 130 }}>
            <h2 style={estiloTitulo || { fontSize: 20, fontWeight: 900, color: '#1e293b', margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Building2 size={20} style={{ verticalAlign: 'middle' }} /> {titulo}
                <span style={{ fontSize: 12, fontWeight: 700, background: '#f1f5f9', color: '#475569', padding: '2px 10px', borderRadius: 20 }}>{lista.length}</span>
            </h2>
            <div style={{ display: 'grid', '--v2-cols': isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gridTemplateColumns: 'var(--v2-cols)', gap: isMobile ? 16 : 24 }}>
                {lista.map(h => <HotelCard key={h.slug} hotel={h} onClick={() => navigate(`/hotel/${h.slug}`)} />)}
            </div>
        </section>
    );
};

// Página /hoteles
const HotelesPagina = () => {
    const hoteles = useHotelesPublicos();
    useEffect(() => { const previo = document.title; document.title = 'Hoteles'; return () => { document.title = previo; }; }, []);
    return (
        <div style={{ minHeight: '70vh', background: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>
            <div style={{ background: '#003366', color: '#fff', padding: '34px 20px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    <h1 style={{ margin: 0, fontSize: 30, fontWeight: 900 }}>Hoteles</h1>
                    <p style={{ margin: '6px 0 0', fontSize: 15, opacity: 0.9, maxWidth: 620 }}>Hoteles aliados, con la reserva y la atención respaldadas por Vyara Group.</p>
                </div>
            </div>
            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 20px 60px' }}>
                {hoteles === null && <p style={{ color: '#64748b' }}>Cargando…</p>}
                {hoteles && hoteles.length === 0 && <p style={{ color: '#64748b' }}>Pronto encontrarás aquí nuestros hoteles.</p>}
                <HotelesSection titulo="Elige tu hotel" />
            </div>
        </div>
    );
};

export default HotelesPagina;
